import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ContractSignArea, Rectangle } from '../../../customers/types/classes';
import type { PDFDocumentProxy, PDFSource } from 'ng2-pdf-viewer';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  Validators
} from '@angular/forms';
import { User } from '@aid/core/types/classes';
import { ContractSignType } from '@aid/customers/types/enums';
import { TranslateService } from '@ngx-translate/core';
import { readField } from '@aid/shared/utils/object-fields';

@Component({
  selector: 'aid-brokerage-contract-sign-configuration-pdf',
  template: `
    <div class="container full-width full-height aid-layout-row">
      @if (loading) {
        <div
          class="loading-shade aid-layout-row aid-layout-align-center-center"
        >
          <mat-spinner diameter="48"></mat-spinner>
        </div>
      }
      <div class="container__pdf" #pdf>
        <div #containerPdf>
          <canvas #canvas [width]="600" height="0"></canvas>
          <pdf-viewer
            #pdfViewer
            style="display: block; width: 600px;"
            [src]="pdfFile"
            [fit-to-page]="true"
            [show-all]="true"
            [autoresize]="false"
            (after-load-complete)="onAfterLoadPdf($event)"
            (pages-initialized)="onPagesInitialized($event)"
            (page-rendered)="pageRendered($event)"
          ></pdf-viewer>
        </div>
      </div>

      @if (!landscape) {
        <div class="flex">
          <div
            class="container__signs full-width aid-layout-column aid-layout-gap-16"
          >
            @if (checkBrokerSignature && !user?.signature) {
              <mat-card
                class="aid-layout-row aid-layout-align-start-center aid-layout-gap-16"
              >
                <div>
                  <mat-icon svgIcon="alert_triangle"></mat-icon>
                </div>
                <span class="mat-subtitle-2 basic-color-900">{{
                  'no-broker-signature' | translate | sentancecase
                }}</span>
              </mat-card>
            }
            @if (form.length > 0) {
              @for (signForm of form.controls; track signForm; let i = $index) {
                <aid-brokerage-contract-sign-area
                  [user]="user"
                  [control]="signForm"
                  [checkBrokerSignature]="checkBrokerSignature"
                  [index]="i + 1"
                  (remove)="onRemoveSign(i)"
                ></aid-brokerage-contract-sign-area>
              }
              <div class="aid-layout-row aid-layout-align-end">
                <aid-loading-button
                  [label]="'save'"
                  [loading]="saving"
                  [disabled]="form.invalid"
                  (press)="onSave()"
                ></aid-loading-button>
              </div>
            }
            @if (form.length === 0) {
              <div
                class="text-center full-height aid-layout-column aid-layout-align-center-center aid-layout-gap-8"
              >
                <div class="mat-headline-6">{{ 'no-signs' | translate }}</div>
                <div class="mat-subheading-1-light">
                  {{ 'no-signs-description' | translate }}
                </div>
              </div>
            }
          </div>
        </div>
      }

      @if (landscape) {
        <div
          class="full-height full-width aid-layout-column aid-layout-align-center-center aid-layout-gap-16"
        >
          <img
            src="assets/background/warning.png"
            alt="no"
            class="container__warning"
          />
          <div class="padding-8 mat-subtitle-2 basic-color-900">
            {{ 'accept-only-A4' | translate }}
          </div>
        </div>
      }
    </div>
  `,
  styleUrls: ['./brokerage-contract-sign-configuration-pdf.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BrokerageContractSignConfigurationPdfComponent implements OnInit {
  @Input() src?: string;
  @Input() file?: File | null;
  @Input() saving?: boolean;
  @Input() user?: User | null;
  @Input() signs?: ContractSignArea[];
  @Input() checkBrokerSignature = true;
  @Output() save = new EventEmitter<ContractSignArea[]>();

  @ViewChild('containerPdf', { static: true })
  containerPdf?: ElementRef<HTMLElement>;
  @ViewChild('pdf', { static: true })
  pdf?: ElementRef<HTMLElement>;
  @ViewChild('canvas', { static: true }) canvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('pdfViewer', { read: ElementRef, static: true })
  pdfViewer?: ElementRef<HTMLElement>;
  ctx?: CanvasRenderingContext2D | null;

  loading = false;
  form: UntypedFormArray = this.formBuilder.array([]);
  pdfFile?: PDFSource;
  landscape = false;

  private mouseDown = false;
  private lastMouseX?: number;
  private lastMouseY?: number;

  /**
   * The width and height for the current rectangle
   */
  private width?: number;
  private height?: number;

  private pdfHeight?: number;
  private numberOfPages?: number;

  private pageHeight?: number;
  private pdfScale?: number;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService
  ) {}

  private initializeCtx() {
    if (this.ctx) {
      return;
    }

    this.ctx = this.canvasElement.getContext('2d');
  }
  ngOnInit() {
    this.setPdfFile();
    this.loading = true;

    this.setSignatures();
    this.initializeCtx();

    this.canvasElement.addEventListener('mousedown', (ev: any) => {
      this.lastMouseX = ev.layerX;
      this.lastMouseY = ev.layerY;
      this.mouseDown = true;
    });

    this.canvasElement.addEventListener('mouseup', ev => {
      this.mouseDown = false;
      this.addRectangle();
      this.drawSignAreas();
    });

    this.canvasElement.addEventListener('mousemove', (ev: any) => {
      const mouseX = ev.layerX;
      const mouseY = ev.layerY;
      if (!this.mouseDown) {
        return;
      }

      this.drawCurrentRectangle(mouseX, mouseY);
    });
  }

  onRemoveSign(index: number) {
    this.form.removeAt(index);
    this.drawSignAreas();
  }

  pageRendered(event: unknown) {
    // pdf.js passes the rendered PDFPageView as `source`; the viewer types the
    // event as a plain CustomEvent.
    const source = readField(event, 'source');
    const viewport = readField(source, 'viewport');
    const viewportWidth = this.renderedNumber(viewport, 'width');
    const viewportHeight = this.renderedNumber(viewport, 'height');

    if (this.checkBrokerSignature && viewportWidth > viewportHeight) {
      this.landscape = true;
      this.loading = false;
      this.rendered(this.pdf, 'pdf').remove();
      return;
    }
    const pageIndex = this.renderedNumber(
      readField(source, 'pdfPage'),
      'pageNumber'
    );

    this.pdfScale = this.renderedNumber(viewport, 'scale');

    if (pageIndex === this.numberOfPages) {
      const viewer = this.rendered(this.pdfViewer, 'pdfViewer');
      // Fit-to-page scaling finishes after pagesinit; refresh the absolute viewer host.
      const document = viewer.querySelector<HTMLElement>('.pdfViewer');
      if (!document) {
        throw new Error('The PDF viewer rendered no document container.');
      }
      viewer.style.height = `${document.scrollHeight}px`;
      const pdfHeight = this.rendered(
        this.containerPdf,
        'containerPdf'
      ).offsetHeight;
      this.pdfHeight = pdfHeight;
      const pages = viewer.querySelectorAll<HTMLElement>('.page');
      // Inter-page margins are absent after the last page, so use the actual page stride.
      this.pageHeight =
        pages.length > 1 ? pages[1].offsetTop - pages[0].offsetTop : pdfHeight;
      this.canvasElement.height = pdfHeight;
      this.loading = false;
      this.drawSignAreas();
    }
  }

  onAfterLoadPdf(event: PDFDocumentProxy) {
    this.numberOfPages = event.numPages;
  }

  onPagesInitialized(event: unknown): void {
    if (typeof event !== 'object' || event === null || !('source' in event)) {
      throw new Error('PDF viewer initialization event is missing its source');
    }
    const source = event.source;
    if (
      typeof source !== 'object' ||
      source === null ||
      !('viewer' in source) ||
      !(source.viewer instanceof HTMLElement) ||
      !('update' in source) ||
      typeof source.update !== 'function'
    ) {
      throw new Error('PDF viewer initialization source has no page geometry');
    }
    // The viewer's absolute container needs the full document height before rendering.
    this.rendered(this.pdfViewer, 'pdfViewer').style.height =
      `${source.viewer.scrollHeight}px`;
    source.update();
  }

  onSave() {
    const pageHeight = this.pageHeight;
    const pdfScale = this.pdfScale;
    if (pageHeight === undefined || pdfScale === undefined) {
      throw new Error(
        'Signs cannot be saved before the PDF pages are measured.'
      );
    }
    const signs: ContractSignArea[] = [];
    this.form.controls.forEach((control, index) => {
      const controlValue = (name: string) => {
        const field = control.get(name);
        if (!field) {
          throw new Error(`A contract sign form has no "${name}" control.`);
        }
        return field.value;
      };
      const area = { ...controlValue('area') };
      const page = controlValue('page');
      const sign = new ContractSignArea();
      sign.description = controlValue('description');
      sign.index = index + 1;
      sign.scale = pdfScale;
      sign.page = page;
      sign.type = controlValue('contractSignType');
      area.y = Math.round(area.y - (page - 1) * pageHeight);
      sign.area = area;
      sign.pageHeight = pageHeight;

      signs.push(sign);
    });

    this.save.emit(signs);
  }

  hasChanges() {
    return this.form.controls.length > 0;
  }

  initForm() {
    this.form = this.formBuilder.array([]);
  }

  setSignatures() {
    if (!this.signs || this.signs.length === 0) {
      return;
    }

    this.signs.forEach(sign => {
      const _form = this.formBuilder.group({
        area: sign.area,
        page: sign.page,
        description: [sign.description, Validators.required],
        contractSignType: [sign.type, Validators.required]
      });

      this.form.push(_form);
    });
  }

  private drawCurrentRectangle(mouseX: number, mouseY: number) {
    this.drawSignAreas();

    // draw the current rectangle
    const lastMouseX = this.lastMouseX;
    const lastMouseY = this.lastMouseY;
    if (lastMouseX === undefined || lastMouseY === undefined) {
      return;
    }
    const width = mouseX - lastMouseX;
    const height = mouseY - lastMouseY;
    this.width = width;
    this.height = height;

    this.drawRectangle({ x: lastMouseX, y: lastMouseY, width, height });
  }

  private drawSignAreas() {
    const canvas = this.canvasElement;
    this.context.clearRect(0, 0, canvas.width, canvas.height);

    // draw the previous rectangles
    for (let i = 0; i < this.form.length; i++) {
      const item: Required<Rectangle> = this.form.at(i).value.area;
      const title = `${this.translateService.instant('sign')} ${i + 1}`;
      this.drawRectangle(item, title);
    }
  }

  private drawRectangle(item: Required<Rectangle>, title?: string) {
    const ctx = this.context;
    ctx.strokeStyle = 'rgba(0, 0, 0, 1)';
    ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
    ctx.lineWidth = 1;

    ctx.beginPath();
    ctx.rect(item.x, item.y, item.width, item.height);
    ctx.fill();
    if (title) {
      ctx.font = '14px sans-serif';
      ctx.fillStyle = 'black';
      ctx.fillText(title, item.x + 5, item.y + 15);
    }

    ctx.stroke();
  }

  private addRectangle() {
    if (this.width !== undefined && this.width < 80) {
      this.width = 80;
    }
    if (this.height !== undefined && this.height < 30) {
      this.height = 30;
    }

    // A mouseup before the first drag, or before the pages are measured (the
    // canvas has no height until then), adds nothing.
    const lastMouseX = this.lastMouseX;
    const lastMouseY = this.lastMouseY;
    const pageHeight = this.pageHeight;
    if (
      lastMouseX === undefined ||
      lastMouseY === undefined ||
      pageHeight === undefined ||
      !this.width ||
      !this.height
    ) {
      return;
    }

    const page = Math.ceil(lastMouseY / pageHeight);
    const rectangle = new Rectangle();
    rectangle.x = lastMouseX;
    rectangle.y = lastMouseY;
    rectangle.width = this.width;
    rectangle.height = this.height;

    const form = this.formBuilder.group({
      area: rectangle,
      page,
      description: ['', Validators.required],
      contractSignType: [ContractSignType.CUSTOMER, Validators.required]
    });
    this.form.push(form);
  }

  private setPdfFile() {
    if (typeof FileReader !== 'undefined') {
      const file = this.file;
      if (!file) {
        throw new Error('The sign configuration needs a PDF file.');
      }
      const reader = new FileReader();

      reader.onload = (e: any) => {
        this.pdfFile = e.target.result;
      };

      reader.readAsArrayBuffer(file);
    }
  }

  private get canvasElement(): HTMLCanvasElement {
    return this.rendered(this.canvas, 'canvas');
  }

  private get context(): CanvasRenderingContext2D {
    if (!this.ctx) {
      throw new Error('The sign canvas has no 2D context.');
    }
    return this.ctx;
  }

  private rendered<T>(ref: ElementRef<T> | undefined, name: string): T {
    if (!ref) {
      throw new Error(`The sign configuration has no "${name}" element.`);
    }
    return ref.nativeElement;
  }

  private renderedNumber(value: unknown, key: string): number {
    const field = readField(value, key);
    if (typeof field !== 'number') {
      throw new Error(`The rendered PDF page has no numeric "${key}".`);
    }
    return field;
  }
}
